/**
 * Stripe Connect onboarding wizard for the Global Loyalty Pool.
 *
 * Layout:
 *   • 5-step setup checklist with traffic-light status + deep links
 *   • Per-tenant onboarding table (Send link / Refresh status / Open Stripe)
 *   • Dry-run + real settlement buttons
 *   • Floating "Ask the Concierge" chat (AI support powered by Claude)
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ExternalLink,
  CheckCircle2,
  Circle,
  RefreshCw,
  Sparkles,
  Send,
  X,
  AlertCircle,
  PlayCircle,
  Banknote,
  Copy,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const tok = () => localStorage.getItem("token") || "";
const H = () => ({ Authorization: `Bearer ${tok()}`, "Content-Type": "application/json" });
const aud = (n) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(Number(n || 0));

/* ─────────────────── Checklist row ─────────────────── */
function Step({ n, title, desc, done, href, hrefLabel = "Open in Stripe", children, testid }) {
  return (
    <li className="flex gap-4 py-4 border-b last:border-b-0 border-stone-100" data-testid={testid}>
      <div className="mt-0.5">
        {done ? (
          <CheckCircle2 className="h-6 w-6 text-emerald-600" strokeWidth={1.75} />
        ) : (
          <Circle className="h-6 w-6 text-stone-300" strokeWidth={1.5} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="text-xs font-semibold text-stone-400 tabular-nums">{n}.</span>
          <span className="font-medium text-stone-900">{title}</span>
        </div>
        <p className="text-sm text-stone-600 mt-1">{desc}</p>
        <div className="mt-2 flex gap-2 flex-wrap">
          {href ? (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`${testid}-link`}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-sky-700 hover:text-sky-900 hover:underline"
            >
              {hrefLabel}
              <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.75} />
            </a>
          ) : null}
          {children}
        </div>
      </div>
    </li>
  );
}

/* ─────────────────── AI Concierge widget ─────────────────── */
function ConciergeWidget({ context }) {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    {
      role: "assistant",
      content:
        "G'day — I'm the HustlePlate Setup Concierge. Ask me anything about Stripe Connect, the loyalty pool, monthly settlements, or what to click next.",
    },
  ]);
  const [draft, setDraft] = useState("");
  const [sid, setSid] = useState(null);
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, open]);

  const send = async () => {
    const text = draft.trim();
    if (!text || busy) return;
    setMsgs((m) => [...m, { role: "user", content: text }]);
    setDraft("");
    setBusy(true);
    try {
      const r = await fetch(`${API}/admin/support/chat`, {
        method: "POST",
        headers: H(),
        body: JSON.stringify({ message: text, session_id: sid, context }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || "Chat failed");
      setSid(j.session_id);
      setMsgs((m) => [...m, { role: "assistant", content: j.reply }]);
    } catch (e) {
      setMsgs((m) => [
        ...m,
        { role: "assistant", content: `Sorry — I hit an error: ${String(e).slice(0, 180)}` },
      ]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="concierge-toggle"
        className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 rounded-full bg-stone-900 text-white px-5 py-3 shadow-lg hover:bg-stone-800 transition-colors"
      >
        <Sparkles className="h-4 w-4" strokeWidth={1.75} />
        <span className="font-medium text-sm">Ask the Concierge</span>
      </button>
      {open ? (
        <div
          data-testid="concierge-panel"
          className="fixed bottom-24 right-6 z-40 w-[380px] max-w-[calc(100vw-2rem)] max-h-[70vh] bg-white rounded-2xl shadow-2xl border border-stone-200 flex flex-col overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100 bg-stone-50">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-amber-600" strokeWidth={1.75} />
              <span className="font-semibold text-stone-900">Setup Concierge</span>
              <Badge variant="secondary" className="text-[10px]">AI · Claude Sonnet 4.5</Badge>
            </div>
            <button onClick={() => setOpen(false)} data-testid="concierge-close">
              <X className="h-4 w-4 text-stone-500 hover:text-stone-900" strokeWidth={2} />
            </button>
          </div>
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-3 space-y-3 text-sm bg-white"
          >
            {msgs.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`rounded-2xl px-3.5 py-2.5 max-w-[85%] whitespace-pre-wrap leading-relaxed ${
                    m.role === "user"
                      ? "bg-stone-900 text-white"
                      : "bg-stone-100 text-stone-900"
                  }`}
                  data-testid={`concierge-msg-${m.role}`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {busy ? (
              <div className="text-xs text-stone-400 italic">Concierge is typing…</div>
            ) : null}
          </div>
          <div className="border-t border-stone-100 p-3 flex gap-2 bg-stone-50">
            <input
              data-testid="concierge-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="What do I do next?"
              className="flex-1 px-3 py-2 rounded-full bg-white border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-300"
            />
            <Button
              data-testid="concierge-send"
              onClick={send}
              disabled={busy || !draft.trim()}
              className="rounded-full h-9 px-3"
            >
              <Send className="h-4 w-4" strokeWidth={2} />
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );
}

/* ─────────────────── Setup Tab ─────────────────── */
export default function LoyaltyConnectSetupTab({ poolState }) {
  const [health, setHealth] = useState(null);
  const [accounts, setAccounts] = useState({ connected: [], pending: [] });
  const [busy, setBusy] = useState(false);
  const [dryRun, setDryRun] = useState(null);

  const refresh = async () => {
    setBusy(true);
    try {
      const [h, a] = await Promise.all([
        fetch(`${API}/admin/global-loyalty/connect/health`, { headers: H() }).then((r) => r.json()),
        fetch(`${API}/admin/global-loyalty/connect/accounts`, { headers: H() }).then((r) => r.json()),
      ]);
      setHealth(h);
      setAccounts(a);
    } catch {
      toast.error("Failed to load setup state");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const links = health?.deep_links || {};

  const stepsDone = useMemo(() => {
    const apiOk = !!health?.ok;
    const platformReady = apiOk && !!health?.charges_enabled;
    const anyTenant = accounts.connected.length > 0;
    const anyActive = accounts.connected.some((a) => a.status === "active");
    return { apiOk, platformReady, anyTenant, anyActive };
  }, [health, accounts]);

  const onboard = async (business_id) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/admin/global-loyalty/connect/onboard`, {
        method: "POST",
        headers: H(),
        body: JSON.stringify({ business_id }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || "onboard failed");
      window.open(j.onboarding_url, "_blank");
      navigator.clipboard?.writeText(j.onboarding_url);
      toast.success("Onboarding link opened + copied to clipboard");
      await refresh();
    } catch (e) {
      toast.error(`Onboard failed: ${String(e).slice(0, 200)}`);
    } finally {
      setBusy(false);
    }
  };

  const refreshAcct = async (business_id) => {
    try {
      await fetch(`${API}/admin/global-loyalty/connect/refresh/${business_id}`, {
        method: "POST",
        headers: H(),
      });
      await refresh();
      toast.success("Status refreshed");
    } catch {
      toast.error("Refresh failed");
    }
  };

  const runSettlement = async (dry = true) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/admin/global-loyalty/connect/settle`, {
        method: "POST",
        headers: H(),
        body: JSON.stringify({ dry_run: dry }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || "settlement error");
      setDryRun(j);
      toast.success(dry ? "Dry-run completed" : `Settled ${aud(j.totals.transferred_aud)}`);
    } catch (e) {
      toast.error(`Settlement failed: ${String(e).slice(0, 200)}`);
    } finally {
      setBusy(false);
    }
  };

  const conciergeContext = {
    stripe_health_ok: health?.ok,
    stripe_error: health?.error,
    connected_tenants: accounts.connected.length,
    pending_tenants: accounts.pending.length,
    pool_balance_aud: poolState?.pool_balance_aud,
  };

  return (
    <div className="space-y-6">
      {/* CHECKLIST */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle className="text-base">Stripe Connect — setup checklist</CardTitle>
            <p className="text-xs text-stone-500 mt-1">
              Five clicks from "logical pool" to real cash transfers. Deep links open the exact
              page in your Stripe dashboard. Stuck? Ask the Concierge bottom-right.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={refresh}
            disabled={busy}
            data-testid="setup-refresh"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${busy ? "animate-spin" : ""}`} />
            Re-check
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <ol className="px-6">
            <Step
              testid="step-api-key"
              n={1}
              title="Stripe API key reachable"
              desc={
                health?.ok
                  ? `Platform account ${health.platform_account_id} · country ${health.country}`
                  : `Backend reports: ${health?.error || "checking…"} — open Stripe API keys and confirm STRIPE_API_KEY in /app/backend/.env matches a real Secret key.`
              }
              done={stepsDone.apiOk}
              href={links.api_keys}
              hrefLabel="Open API keys"
            />
            <Step
              testid="step-connect-on"
              n={2}
              title="Enable Connect (Platform mode)"
              desc="Toggle Connect ON, fill the Platform profile, and upload branding so tenants see your name in the onboarding flow."
              done={stepsDone.apiOk && health?.charges_enabled}
              href={links.connect_settings}
              hrefLabel="Open Connect settings"
            >
              <a
                href={links.platform_profile}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-sky-700 hover:underline"
              >
                Platform profile <ExternalLink className="h-3 w-3" />
              </a>
              <a
                href={links.branding}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-sky-700 hover:underline"
              >
                Branding <ExternalLink className="h-3 w-3" />
              </a>
            </Step>
            <Step
              testid="step-webhook"
              n={3}
              title="Add Connect webhook endpoint"
              desc={`Listen for account.updated, payout.paid, transfer.created and transfer.failed. URL: ${process.env.REACT_APP_BACKEND_URL}/api/webhook/stripe`}
              done={false}
              href={links.webhooks}
              hrefLabel="Open Webhooks"
            >
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard?.writeText(
                    `${process.env.REACT_APP_BACKEND_URL}/api/webhook/stripe`
                  );
                  toast.success("Webhook URL copied");
                }}
                data-testid="copy-webhook"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-stone-700 hover:underline"
              >
                <Copy className="h-3.5 w-3.5" /> Copy URL
              </button>
            </Step>
            <Step
              testid="step-tenant-onboard"
              n={4}
              title="Onboard each tenant restaurant"
              desc="Send each tenant a Stripe Express onboarding link. They add their ABN + bank, Stripe approves, status flips to Active."
              done={stepsDone.anyActive}
            >
              <span className="text-xs text-stone-500">
                {accounts.connected.length} started · {accounts.connected.filter((a) => a.status === "active").length} active · {accounts.pending.length} not started
              </span>
            </Step>
            <Step
              testid="step-settle"
              n={5}
              title="Run the monthly settlement"
              desc="Each month, transfer net obligations from the pool to each tenant's connected account. Always do a Dry-run first."
              done={false}
            >
              <Button
                size="sm"
                variant="outline"
                onClick={() => runSettlement(true)}
                disabled={busy}
                data-testid="settle-dry"
              >
                <PlayCircle className="h-4 w-4 mr-1.5" /> Dry-run
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  if (window.confirm("Run REAL settlement? This sends actual Stripe Transfers.")) {
                    runSettlement(false);
                  }
                }}
                disabled={busy || !stepsDone.anyActive}
                data-testid="settle-real"
              >
                <Banknote className="h-4 w-4 mr-1.5" /> Run settlement
              </Button>
            </Step>
          </ol>
        </CardContent>
      </Card>

      {/* TENANT ONBOARDING TABLE */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tenant connected accounts</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="text-left text-stone-500 border-b border-stone-100">
              <tr>
                <th className="px-4 py-2">Business</th>
                <th className="px-4 py-2">Stripe account</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Charges</th>
                <th className="px-4 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {accounts.connected.map((a) => (
                <tr key={a.business_id} className="border-b border-stone-50" data-testid={`connected-${a.business_id}`}>
                  <td className="px-4 py-2 text-stone-700">{a.business_name}</td>
                  <td className="px-4 py-2 font-mono text-xs text-stone-500">{a.stripe_account_id}</td>
                  <td className="px-4 py-2">
                    <Badge
                      variant="secondary"
                      className={
                        a.status === "active"
                          ? "bg-emerald-50 text-emerald-700"
                          : a.status === "rejected"
                          ? "bg-rose-50 text-rose-700"
                          : "bg-amber-50 text-amber-700"
                      }
                    >
                      {a.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-2">
                    {a.charges_enabled ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-amber-500" />
                    )}
                  </td>
                  <td className="px-4 py-2 flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => onboard(a.business_id)}>
                      Resend link
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => refreshAcct(a.business_id)}>
                      Refresh
                    </Button>
                  </td>
                </tr>
              ))}
              {accounts.pending.map((p) => (
                <tr key={p.business_id} className="border-b border-stone-50" data-testid={`pending-${p.business_id}`}>
                  <td className="px-4 py-2 text-stone-700">{p.business_name}</td>
                  <td className="px-4 py-2 text-stone-400 text-xs">—</td>
                  <td className="px-4 py-2">
                    <Badge variant="secondary">not started</Badge>
                  </td>
                  <td className="px-4 py-2 text-stone-400">—</td>
                  <td className="px-4 py-2">
                    <Button
                      size="sm"
                      onClick={() => onboard(p.business_id)}
                      data-testid={`onboard-${p.business_id}`}
                      disabled={busy}
                    >
                      Send onboarding link
                    </Button>
                  </td>
                </tr>
              ))}
              {accounts.connected.length === 0 && accounts.pending.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-stone-400">
                    No tenants in the businesses collection yet.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* DRY-RUN PREVIEW */}
      {dryRun ? (
        <Card data-testid="settle-preview">
          <CardHeader>
            <CardTitle className="text-base">
              {dryRun.dry_run ? "Dry-run preview" : "Settlement results"} · {dryRun.month_key}
            </CardTitle>
            <p className="text-xs text-stone-500">
              Total transferred: <span className="font-semibold">{aud(dryRun.totals.transferred_aud)}</span> ·
              Already settled: {aud(dryRun.totals.already_settled_aud)} ·
              Failed: {aud(dryRun.totals.failed_aud)}
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="text-left text-stone-500 border-b border-stone-100">
                <tr>
                  <th className="px-4 py-2">Business</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Reference</th>
                </tr>
              </thead>
              <tbody>
                {dryRun.results.map((r) => (
                  <tr key={r.business_id} className="border-b border-stone-50">
                    <td className="px-4 py-2 text-stone-700">{r.business_name}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{aud(r.amount_aud)}</td>
                    <td className="px-4 py-2">{r.status}</td>
                    <td className="px-4 py-2 font-mono text-xs text-stone-500">
                      {r.stripe_transfer_id || r.error || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ) : null}

      {/* AI CONCIERGE — floating button + slide-up panel */}
      <ConciergeWidget context={conciergeContext} />
    </div>
  );
}
